/**
 * Approval Card Component
 * Individual evidence card in the approval queue
 */
import React from 'react';
import { 
  FileText,
  FileSpreadsheet,
  Image,
  File,
  User,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Link2,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApprovalChainVisualizer from './ApprovalChainVisualizer';

const FILE_ICONS = {
  document: FileText,
  spreadsheet: FileSpreadsheet,
  image: Image,
  pdf: FileText,
  default: File
};

export default function ApprovalCard({ 
  item, 
  onApprove, 
  onReject, 
  onViewEvidence,
  isSelected,
  onSelect
}) {
  const navigate = useNavigate();
  const FileIcon = FILE_ICONS[item.evidenceType] || FILE_ICONS.default;
  const isOverdue = item.sla?.isOverdue;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    return `${diffDays} days ago`;
  };
  
  const handleViewClick = () => {
    if (onViewEvidence) {
      onViewEvidence(item);
    } else {
      navigate(`/evidence/${item.id}`);
    }
  };
  
  return (
    <div 
      className={`
        bg-[#111111] border rounded-xl p-4 transition-all
        ${isOverdue ? 'border-red-500/50 hover:border-red-500/80' : 'border-[#1F1F1F] hover:border-emerald-500/30'}
        ${isSelected ? 'ring-2 ring-emerald-500/50' : ''}
      `}
      data-testid={`approval-card-${item.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Selection Checkbox */}
        <div className="pt-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect && onSelect(item.id)}
            className="w-4 h-4 rounded border-[#3F3F3F] bg-[#1F1F1F] text-emerald-500 focus:ring-emerald-500/50"
            data-testid={`select-checkbox-${item.id}`}
          />
        </div>
        
        {/* File Icon */}
        <div className={`p-3 rounded-lg ${isOverdue ? 'bg-red-500/10' : 'bg-[#1F1F1F]'}`}>
          {isOverdue ? (
            <AlertTriangle size={20} className="text-red-400" />
          ) : (
            <FileIcon size={20} className="text-[#6B6B6B]" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title & Priority */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-white truncate" title={item.fileName}>
              {item.fileName}
            </h3>
            {isOverdue && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-red-500/20 text-red-400 animate-pulse">
                OVERDUE
              </span>
            )}
            {item.category && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-blue-500/20 text-blue-400">
                {item.category}
              </span>
            )}
          </div>
          
          {/* Owner & Upload Info */}
          <div className="flex items-center gap-4 text-xs text-[#6B6B6B] mb-2">
            <span className="flex items-center gap-1">
              <User size={10} />
              Owner: <span className="text-white">{item.owner?.name || 'Unknown'}</span>
              {item.owner?.role && (
                <span className="text-[#4F4F4F]">({item.owner.role})</span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={10} />
              Uploaded {formatDate(item.uploadedAt)} ({formatTimeAgo(item.uploadedAt)})
            </span>
          </div>
          
          {/* Due Date / SLA */}
          {item.sla?.targetDate && (
            <div className={`flex items-center gap-2 text-xs mb-2 ${isOverdue ? 'text-red-400' : 'text-amber-400'}`}>
              <Clock size={10} />
              {isOverdue ? (
                <span>Due: {formatDate(item.sla.targetDate)} ({item.sla.daysOverdue || item.sla.days} days overdue)</span>
              ) : (
                <span>Due: {formatDate(item.sla.targetDate)} ({item.sla.days} days remaining)</span>
              )}
            </div>
          )}
          
          {/* Framework Mappings */}
          {item.frameworks && item.frameworks.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-[#6B6B6B] mb-3">
              <Link2 size={10} />
              <span>Mapped to:</span>
              <div className="flex flex-wrap gap-1">
                {item.frameworks.slice(0, 3).map((fw, idx) => (
                  <span key={idx} className="px-2 py-0.5 rounded bg-[#1F1F1F] text-emerald-400 text-[10px]">
                    {fw}
                  </span>
                ))}
                {item.frameworks.length > 3 && (
                  <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#6B6B6B] text-[10px]">
                    +{item.frameworks.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Approval Chain Visualizer */}
          <ApprovalChainVisualizer chain={item.approvalChain} currentLevel={item.currentApprovalLevel} />
          
          {/* Comment from Previous Approver */}
          {item.lastComment && (
            <div className="mt-3 p-2 rounded-lg bg-[#1A1A1A] border border-[#2F2F2F]">
              <div className="flex items-start gap-2">
                <MessageSquare size={12} className="text-blue-400 mt-0.5" />
                <div className="text-xs">
                  <span className="text-blue-400 font-medium">{item.lastComment.from}:</span>
                  <span className="text-[#A0A0A0] ml-1">"{item.lastComment.text}"</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Status Badge */}
        <div className="flex flex-col items-end gap-2">
          <ApprovalStatusBadge status={item.approvalStatus} />
          <span className="text-[10px] text-[#6B6B6B]">
            Level {item.currentApprovalLevel}/3
          </span>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#1F1F1F]">
        <button 
          onClick={handleViewClick}
          className="flex-1 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
          data-testid={`view-evidence-btn-${item.id}`}
        >
          <Eye size={12} />
          View Evidence
        </button>
        <button 
          onClick={() => onApprove && onApprove(item)}
          className="flex-1 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
          data-testid={`approve-btn-${item.id}`}
        >
          <CheckCircle2 size={12} />
          Approve
        </button>
        <button 
          onClick={() => onReject && onReject(item)}
          className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
          data-testid={`reject-btn-${item.id}`}
        >
          <XCircle size={12} />
          Reject
        </button>
      </div>
    </div>
  );
}

function ApprovalStatusBadge({ status }) {
  const config = {
    pending: { label: 'Pending', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
    pending_l1: { label: 'L1 Pending', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
    pending_l2: { label: 'L2 Pending', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
    pending_l3: { label: 'L3 Pending', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
    approved: { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
    rejected: { label: 'Rejected', bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    changes_requested: { label: 'Changes Requested', bg: 'bg-purple-500/20', text: 'text-purple-400', icon: MessageSquare },
  };
  
  const cfg = config[status] || config.pending;
  const StatusIcon = cfg.icon;
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded flex items-center gap-1 ${cfg.bg} ${cfg.text}`}>
      <StatusIcon size={12} />
      {cfg.label}
    </span>
  );
}
